/**
 * engine/fluxo.ts — o FLUXO MENSAL pelo "Memorial de calculo FinCast" (Projeções e IGH).
 * Fluxo pelo memorial, lote 1 (15/10/2026) — plano em `PLANO-FLUXO-MEMORIAL.md`, decisões D1–D11
 * e padrões P1–P10 transcritos no Registro.md.
 *
 * O que o memorial descreve, na ordem em que acontece (granularidade MENSAL — D1):
 *   1. no mês da demanda entram as unidades da demanda e as garantias: terreno (o valor de hoje,
 *      `c.valorTerrenoGar` — D6), aporte e antecipação PJ (o % do PJ de hoje — D8);
 *   2. as garantias são consumidas SEMPRE na ordem terreno → aporte → antecipação: pelo custo de
 *      cada medição (CTO × % medido; o PLS0 é a linha de base e não gera custo) e pelo saldo de FSF
 *      de cada unidade NOVA que assina. Só o aporte liberado volta em caixa;
 *   3. enquanto há garantia, o FSF das medições fica RETIDO (acumulado). Depois que elas zeram, o
 *      custo de cada medição é pago primeiro com o FSF acumulado, liberado para a incorporadora;
 *   4. o que falta é o PJ disponibilizado; somado à recomposição (o FSF bloqueado das unidades novas
 *      assinadas e não registradas), é o PJ calculado — que só ENTRA se o IGH da medição estiver
 *      conforme (D9: não conforme BLOQUEIA; o custo fica descoberto, sem passar ao mês seguinte — P2);
 *   5. o PJ entra no saldo devedor, que paga juros todo mês (`engine/juros.ts`). O FSF da unidade nova
 *      fica BLOQUEADO até o registro (21/10/2026): no registro ele entra no caixa ("FSF › Desbloqueio no
 *      registro") e, na nova que não quitou garantia, saldo × medição acumulada amortiza o PJ;
 *   6. (15/10/2026) terminada a obra (100% nas Premissas Mensais), o PJ é pago em parcelas (EL-210/EL-211:
 *      começa N meses depois, em M parcelas de saldo ÷ restantes) e a tabela se estende até quitar; a
 *      carência soma um adicional aos juros em 3 ciclos de x meses depois do término (EL-212 a EL-215);
 *   7. (16/10/2026) no REGISTRO de cada unidade entra a remuneração dela (`engine/remuneracao.ts`, pelo
 *      memorial da remuneração das unidades): Poupança + TR sobre a fração de terreno e sobre o FSF já
 *      medido, da assinatura ao registro, menos o IR regressivo — "Receitas Financeiras" › "Remuneração
 *      terreno" e "Remuneração obra" (líquidas), somando no caixa (resposta "Na linha, somando no caixa").
 *
 * Ordem dos eventos dentro do mês (convenção da granularidade mensal — P4, a medição é no último dia):
 *   assinaturas → processamentos → registros → medição. Os juros do mês incidem sobre o saldo de
 *   ABERTURA (o que entra no mês começa a pagar no seguinte).
 *
 * Puro: sem DOM, sem relógio, não escreve nos argumentos.
 */

import { addDays, primeiroDia, ymOf } from './datas';

/** o último dia do mês `ym`, em ISO (o dia da medição — P4) */
const ultimoDia = (ym: Ym): IsoDate => addDays(primeiroDia(ym + 1), -1);
import { dataFinanciamentoEfetiva, premissasMensaisSemVendasFin, vendasDetZeradas, vendasMensais } from './demanda';
import { fsfDetalheDe, ladoDetalhamento, repasseDaUnidade } from './garantias';
import { _saiuEstoqueTempo, estoqueNoMes } from './igh';
import { jurosDoMes, paramJurosDe, type TaxasMensais } from './juros';
import { medicoesDoFluxo, remuneracaoDaUnidade, taxaRemuneracao } from './remuneracao';
import type { Empreendimento, IsoDate, Lev, ResultadoBase, ResultadoFluxo, SeriesFluxo, Unidade, Ym } from './tipos';

/**
 * Parâmetros do fluxo que não são campo de tela.
 *
 * As três constantes provisórias do v67 SAÍRAM: os juros (0,8% a.m.) vêm de `engine/juros.ts`, a
 * amortização (5% a.m.), dos registros e das parcelas, e as Receitas Financeiras (0,4% a.m. sobre o
 * caixa acumulado — `rendCaixaMes`, mantida pela D3 até 16/10/2026), da remuneração das unidades
 * (`engine/remuneracao.ts`).
 */
export interface ConfigFluxo {
  /** memorial: assinatura = data de venda (CCV) + prazo de assinatura (padrão 30 dias) */
  prazoAssinaturaDias: number;
  /** memorial: processamento do FSF = assinatura + prazo (padrão 2 dias) */
  prazoProcessamentoDias: number;
  /** % aceito de cada PJ calculado (memorial: "PJ liberado será sempre aceito?" Sim = 100%) */
  pctAceito: number;
  /** séries do Banco Central (+ a projeção, só para os juros); `null` = sem taxas carregadas */
  taxas: TaxasMensais | null;
}

export const CONFIG_FLUXO_PADRAO: ConfigFluxo = {
  prazoAssinaturaDias: 30, prazoProcessamentoDias: 2, pctAceito: 100, taxas: null,
};

/* ============================================================================
 * 1. As unidades do fluxo (plano, 3.1)
 * ========================================================================== */

/**
 * Um LOTE de unidades que entra no fluxo junto: uma unidade do Detalhamento, ou um grupo "pela
 * média" (as "Vendas Fin." que nenhuma unidade identificada explica, ou o campo EL-057).
 * `qtd` e `saldo` podem ser NEGATIVOS num lote pela média quando a conciliação troca unidades pela
 * média por unidades identificadas (a mesma conta da barra: `_fsfTerrenoDaBarra`).
 */
export interface LoteFluxo {
  origem: 'unidade' | 'media' | 'campo';
  /** posição em `lev.unidadesDet` (só `origem: 'unidade'`) */
  i: number | null;
  nome: string;
  qtd: number;
  /** saldo de financiamento (FSF) do lote = Σ máx(0, base EL-191 × % − fração do terreno) */
  saldo: number;
  /** fração do terreno do lote, em reais — recebida no registro */
  terreno: number;
  /** mês em que o lote entra na barra (o mês da venda, ou o da demanda) */
  ymEntrada: Ym;
  daDemanda: boolean;
  dataAssinatura: IsoDate;
  /** assinatura + EL-146 (a recomposição compara a data, como o código de origem) */
  dataRegistro: IsoDate;
  ymAssinatura: Ym;
  ymProcessamento: Ym;
  ymRegistro: Ym;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const valida = (d: unknown): d is IsoDate => typeof d === 'string' && ISO.test(d);
const maior = (a: IsoDate, b: IsoDate): IsoDate => (a >= b ? a : b);

/**
 * A data da demanda (o "Cumprimento de demanda", EL-065) quando ela cai no mês da demanda do fluxo;
 * senão o 1º dia desse mês. Na tabela embaixo de cada barra, o mês da demanda é o da barra.
 */
export function dataDaDemanda(lev: Lev, ymDem: Ym): IsoDate {
  const d = lev.dataDemandaDesejada;
  return valida(d) && ymOf(d) === ymDem ? d : primeiroDia(ymDem);
}

/**
 * As unidades (em lotes) que formam o fluxo, com as datas do memorial. QUANTAS formam a demanda e
 * quais entram em cada mês é a MESMA conta da barra do mês (`_fsfTerrenoDaBarra`, decisões do lote 4
 * de 10/10/2026 e de 11/10/2026), que o fluxo de antes também seguia:
 *   - SEM nenhuma "Vendas Fin." (`premissasMensaisSemVendasFin`): a demanda é o campo EL-057 × a média;
 *   - Detalhamento zerado: as "Vendas Fin." de cada mês, pela média (repasse médio e terreno ÷ unidades);
 *   - Detalhamento preenchido (modo individual — "um caminho OU outro", 20/10/2026): só as unidades
 *     vendidas financiadas, cada uma no mês da venda, sem lote pela média (acabou a conciliação).
 * Da demanda = o que já está na barra do mês da demanda; assina na data da demanda (memorial: "as
 * unidades com a menor data de assinatura"). As demais são NOVAS: assinam na "Data de Financiamento"
 * que vale (`dataFinanciamentoEfetiva`), senão na data de venda + prazo (30 dias) — nunca antes da
 * demanda, nem antes do mês em que entram. Processamento = assinatura + 2 dias; registro =
 * assinatura + EL-146 (`lev.tempoRegistro`).
 */
export function unidadesDoFluxo(b: Empreendimento, lev: Lev, c: ResultadoBase, cfg: ConfigFluxo = CONFIG_FLUXO_PADRAO): LoteFluxo[] {
  if (c.ymDem == null) return [];
  const ymDem = c.ymDem;
  const meses = Array.isArray(lev.meses) ? lev.meses : [];
  const dDem = dataDaDemanda(lev, ymDem);
  const media = repasseDaUnidade(c.preco, lev.pctFin, c.fracao);
  const tReg = (lev && lev.tempoRegistro) || 0;
  const datas = (assin: IsoDate) => {
    const proc = addDays(assin, cfg.prazoProcessamentoDias || 0);
    const reg = tReg > 0 ? addDays(assin, tReg) : assin;
    return { dataAssinatura: assin, dataRegistro: reg, ymAssinatura: ymOf(assin) as Ym, ymProcessamento: ymOf(proc) as Ym, ymRegistro: ymOf(reg) as Ym };
  };
  const daDemanda = (): Pick<LoteFluxo, 'daDemanda' | 'dataAssinatura' | 'dataRegistro' | 'ymAssinatura' | 'ymProcessamento' | 'ymRegistro'> =>
    ({ daDemanda: true, ...datas(dDem) });
  const nova = (ymEntrada: Ym, propria: IsoDate | null) => {
    const base = propria ?? addDays(primeiroDia(ymEntrada), cfg.prazoAssinaturaDias || 0);
    return { daDemanda: false, ...datas(maior(maior(base, dDem), primeiroDia(ymEntrada))) };
  };
  /* a assinatura da unidade NOVA do Detalhamento (revisão do PR SAAS-984, comentário 5, 20/10/2026):
       - com "Data de Financiamento" informada, ela vale — mas nunca antes da data de venda;
       - quem "Ingressa" sem Data de Financiamento herda o Cumprimento (`dataFinanciamentoEfetiva`): vale o maior
         entre ele e a venda + prazo de assinatura (antes, a unidade vendida depois do Cumprimento assinava antes
         da própria venda);
       - sem data nenhuma: a venda + prazo de assinatura (30 dias). */
  const assinaturaDaUnidade = (u: Unidade): IsoDate | null => {
    const venda = valida(u.dataVenda) ? u.dataVenda : null;
    const vendaMaisPrazo = venda ? addDays(venda, cfg.prazoAssinaturaDias || 0) : null;
    const propria = dataFinanciamentoEfetiva(lev, u);
    if (!valida(propria)) return vendaMaisPrazo;
    const herdada = !!(u.ingressaDemanda && u.financiada && !u.permutada);
    if (herdada) return vendaMaisPrazo ? maior(propria, vendaMaisPrazo) : propria;
    return venda ? maior(propria, venda) : propria;
  };
  const loteMedia = (qtd: number, ymEntrada: Ym, dem: boolean, origem: 'media' | 'campo' = 'media'): LoteFluxo => ({
    origem, i: null, nome: origem === 'campo' ? 'Unidades financiadas na demanda (média)' : 'Vendas Fin. pela média',
    qtd, saldo: qtd * media, terreno: qtd * c.fracao, ymEntrada,
    ...(dem ? daDemanda() : nova(ymEntrada, null)),
  });

  const lotes: LoteFluxo[] = [];

  /* 20/10/2026 — "um caminho OU outro" (revisão do PR SAAS-984, comentário 1). MODO RÁPIDO (Detalhamento sem
     vendas): só lotes pela média — o campo da demanda quando não há nenhuma "Vendas Fin." (decisão de
     11/10/2026, "O campo da demanda"), senão as "Vendas Fin." de cada mês. */
  if (vendasDetZeradas(lev)) {
    if (premissasMensaisSemVendasFin(lev)) {
      const q = lev.unidFinDem || 0;
      return q ? [loteMedia(q, ymDem, true, 'campo')] : [];
    }
    const repAte = (ym: Ym) => meses.reduce((s, m) => s + (m.ym <= ym ? (m.vendFin || 0) : 0), 0);
    const qDem = repAte(ymDem);
    if (qDem) lotes.push(loteMedia(qDem, ymDem, true));
    for (const m of meses) if (m.ym > ymDem && (m.vendFin || 0) !== 0) lotes.push(loteMedia(m.vendFin, m.ym, false));
    return lotes;
  }

  /* MODO INDIVIDUAL (Detalhamento com vendas): só lotes de unidades, com as datas delas — sem lote pela média e,
     portanto, sem lote negativo (era a conciliação com as "Vendas Fin.", que contava a venda duas vezes). Da
     demanda = as vendidas até o mês da demanda (a barra); as demais entram no mês da venda. O valor de cada
     unidade (base, %, fração) não depende de quem mais entra — `fsfDetalheDe` com todas as candidatas. */
  const lado = ladoDetalhamento(lev);
  const candidatas: Array<{ i: number; ymV: number; d: string }> = [];
  lado.unidades.forEach((u, i) => {
    if (!u || !u.financiada || u.permutada || !u.dataVenda) return;
    const y = ymOf(u.dataVenda);
    if (y != null && Number.isFinite(y)) candidatas.push({ i, ymV: y, d: u.dataVenda });
  });
  const ehCandidata = new Set(candidatas.map(x => x.i));
  const det = fsfDetalheDe(b, lev, lado, (_u, i) => ehCandidata.has(i));
  const item = new Map(det.itens.map(x => [x.i, x]));
  const ordenadas = candidatas.slice().sort((x, y) => (x.ymV - y.ymV) || (x.d < y.d ? -1 : x.d > y.d ? 1 : 0) || (x.i - y.i));
  const entrou = new Set<number>();
  let primeiro = true;
  const pontos = [ymDem, ...meses.map(m => m.ym).filter(y => y > ymDem)];
  for (const ym of pontos) {
    const dem = primeiro;
    for (const x of ordenadas) {
      if (x.ymV > ym || entrou.has(x.i)) continue;
      entrou.add(x.i);
      const it = item.get(x.i)!;
      const u = lado.unidades[x.i] as Unidade;
      lotes.push({
        origem: 'unidade', i: x.i, nome: u.nome || '', qtd: 1, saldo: it.repasse, terreno: it.terreno, ymEntrada: ym,
        ...(dem ? daDemanda() : nova(ym, assinaturaDaUnidade(u))),
      });
    }
    primeiro = false;
  }
  return lotes;
}

/* ============================================================================
 * 2. O laço mensal (plano, 3.2 a 3.6)
 * ========================================================================== */

/**
 * O IGH de uma medição (memorial do IGH, seções 2 a 7).
 *
 * ⚠ 15/10/2026 — decisão do usuário (caixa, resposta literal "No fluxo: o menor dos dois (Recomendado)"):
 * o mínimo é o MENOR entre o do memorial, `1,30 × (SMPJ + saldo devedor + PJ da parcela)`, e `1,30 × o PJ
 * de referência` — o PJ da barra (o mesmo 1,30 × PJ das barras, das Premissas e do relatório). Sem PJ de
 * referência (`null`), vale só o do memorial.
 */
export function ighDaMedicao(
  CTO: number, medAc: number, fsfProcessado: number, saldoDevedor: number, pjParcela: number, estoque: number,
  pjReferencia: number | null = null,
) {
  const ac = Math.min(1, Math.max(0, medAc));
  const GTO = Math.max(0, CTO * (1 - ac));
  const SMPF = fsfProcessado * (1 - ac);
  const SMPJ = Math.max(0, GTO - SMPF);
  const minimoMemorial = 1.30 * (SMPJ + saldoDevedor + pjParcela);
  /* revisão do PR SAAS-984, comentário 3 (20/10/2026, "Só o memorial (Recomendado)"): o 1,3 × PJ da barra só entra
     na comparação quando a barra TEM PJ; com PJ da barra zero (ou sem barra), vale o mínimo do memorial */
  const minimoPJ = pjReferencia != null && Number.isFinite(pjReferencia) && pjReferencia > 0 ? 1.30 * pjReferencia : null;
  const minimo = minimoPJ == null ? minimoMemorial : Math.min(minimoMemorial, minimoPJ);
  return { GTO, SMPF, SMPJ, minimoMemorial, minimoPJ, minimo, estoque, validacao: estoque - minimo, conforme: estoque >= minimo - 0.5 };
}

/* ============================================================================
 * 3. Amortização e carência (memorial, seção 5 — 15/10/2026)
 * ========================================================================== */

export interface ParamAmortizacao {
  /** meses entre o término e a 1ª parcela (EL-210, padrão 12) */
  inicioMeses: number;
  /** número de parcelas (EL-211, padrão 36) */
  parcelas: number;
  /** duração de cada um dos 3 ciclos de carência, em meses (EL-212; 0 = sem carência) */
  cicloMeses: number;
  /** adicional de juros de cada ciclo, % a.a. (EL-213 a EL-215) */
  adicionais: [number, number, number];
}

const inteiro = (v: unknown, padrao: number): number =>
  (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : padrao);
const numero = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * Os parâmetros de amortização e carência do cenário. Pedido de 15/10/2026 (resposta literal): "so precisa ser
 * personalizado eses 12 / 36 mas precisa estender ate quitar" — os padrões do memorial, editáveis; e a carência
 * "3 ciclos de x meses sendo x o periodo de meses", em aberto (duração e adicionais digitados).
 */
export function paramAmortizacaoDe(lev: Lev): ParamAmortizacao {
  return {
    inicioMeses: inteiro(lev.amortInicioMeses, 12),
    parcelas: Math.max(1, inteiro(lev.amortParcelas, 36)),
    cicloMeses: inteiro(lev.carenciaCicloMeses, 0),
    adicionais: [numero(lev.carenciaAdicional1), numero(lev.carenciaAdicional2), numero(lev.carenciaAdicional3)],
  };
}

/**
 * O TÉRMINO DA OBRA: o 1º mês em que a obra acumulada das Premissas Mensais (PLS0 + Σ "Obra PLS %") chega a
 * 100% (decisão de 15/10/2026, "Quando a obra chega a 100% (Recomendado)"; o memorial usa o campo "Medição 100%
 * PLS", senão o CVCO). `null` = não chega — sem término, o PJ não tem parcelas.
 */
export function ymTerminoObra(lev: Lev): Ym | null {
  let ac = (lev.pls0Pct || 0) / 100;
  for (const m of Array.isArray(lev.meses) ? lev.meses : []) {
    ac += (m.obra || 0) / 100;
    if (ac >= 1 - 1e-9) return m.ym;   /* folga de resíduo binário (a mesma do CAL-2b) */
  }
  return null;
}

/** O adicional de carência (% a.a.) do mês: ciclo k = ⌈(meses depois do término) ÷ duração⌉, k = 1, 2, 3. */
export function adicionalDeCarencia(pa: ParamAmortizacao, ymTermino: Ym | null, ym: Ym): number {
  if (ymTermino == null || pa.cicloMeses <= 0 || ym <= ymTermino) return 0;
  const k = Math.ceil((ym - ymTermino) / pa.cicloMeses);
  return k >= 1 && k <= 3 ? pa.adicionais[k - 1] : 0;
}

interface EstadoLote extends LoteFluxo {
  /** quanto de garantia o saldo do lote quitou na assinatura (`garantias_consumidas` do código de origem) */
  consumido: number;
  processada: boolean;
  registrada: boolean;
  /** a parte do FSF acumulado (retido) que é deste lote — a liberação sai na proporção (`fracaoLiberada` da origem) */
  acum: number;
  /** FSF deste lote já liberado na medição mas BLOQUEADO até o registro (unidade nova ainda não registrada) */
  bloq: number;
}

/**
 * O fluxo mensal. `c` é a saída do `calcBase` — na tabela embaixo de cada barra, a tela troca o mês
 * da demanda, a antecipação e o terreno em garantia pelos da barra (`renderFluxoBarra`).
 * Devolve `null` sem mês da demanda ou sem Premissas Mensais (como antes).
 */
export function calcFluxoMemorial(b: Empreendimento, lev: Lev, c: ResultadoBase, cfg: ConfigFluxo = CONFIG_FLUXO_PADRAO): ResultadoFluxo | null {
  if (c.ymDem == null || !lev.meses.length) return null;
  const ymDem = c.ymDem;
  const start = lev.meses[0].ym;
  const CTO = c.CTO;
  const obraAt = (ym: Ym) => { const m = lev.meses.find(x => x.ym === ym); return m ? (m.obra || 0) / 100 : 0; };
  const vendas = vendasMensais(lev);   /* 20/10/2026: as vendas que valem ("um OU outro") */
  const vendFinCum = (ym: Ym) => vendas.reduce((s, m) => s + (m.ym <= ym ? (m.vendFin || 0) : 0), 0);
  const params = paramJurosDe(lev);
  const lotes: EstadoLote[] = unidadesDoFluxo(b, lev, c, cfg).map(l => ({ ...l, consumido: 0, processada: false, registrada: false, acum: 0, bloq: 0 }));
  /* amortização e carência (15/10/2026): a 1ª parcela vem EL-210 meses depois do término; o horizonte da tabela
     se ESTENDE além das Premissas Mensais até a última parcela (e os registros que caírem até lá) — "precisa
     estender ate quitar". Sem término, fica como era: até o último mês das Premissas Mensais. */
  const pa = paramAmortizacaoDe(lev);
  const ymTermino = ymTerminoObra(lev);
  const ymPrimeiraParcela = ymTermino == null ? null : ymTermino + pa.inicioMeses;
  const ultimoDasPremissas = lev.meses[lev.meses.length - 1]!.ym;
  let ultimo = ultimoDasPremissas;
  if (ymPrimeiraParcela != null) {
    ultimo = Math.max(ultimo, ymPrimeiraParcela + pa.parcelas - 1);
    for (const l of lotes) if (Number.isFinite(l.ymRegistro)) ultimo = Math.max(ultimo, l.ymRegistro);
  }
  const N = Math.max(1, Math.min(ultimo, ultimoDasPremissas + 600) - start + 1);   /* teto de 50 anos além das Premissas */
  const pjReferencia = typeof c.PJ === 'number' && Number.isFinite(c.PJ) ? c.PJ : null;
  /* O ESTOQUE DO IGH NO FLUXO sai no registro que o PRÓPRIO fluxo projeta (15/10/2026 — resposta literal "No
     registro (memorial)"): a unidade que o fluxo registra (a fração do terreno entra no caixa, o FSF bloqueado
     amortiza) sai do estoque no mesmo mês, inclusive a que não tem "Data de Financiamento" (assina na venda +
     30 dias). Antes o estoque usava a régua das barras e ela nunca saía. Detalhamento zerado: (unidades − lotes
     registrados até o mês − permutadas) × VGV ÷ unidades — as "Vendas Fin." saem no registro, não na venda. As
     barras, as Premissas e o relatório seguem com a régua deles (D5). */
  const regDoLote = new Map(lotes.filter(l => l.origem === 'unidade' && l.i != null).map(l => [l.i as number, l.ymRegistro]));
  const estoqueDoFluxo = (ym: Ym): number => {
    if (vendasDetZeradas(lev)) {
      const U = b.unidades || 0, preco = U ? b.VGV / U : 0;
      const registradas = lotes.reduce((s, l) => s + (l.ymRegistro <= ym ? l.qtd : 0), 0);
      return Math.max(0, U - registradas - (b.permutadas || 0)) * preco;
    }
    return estoqueNoMes(b, lev, ym, (u, i) => {
      const reg = regDoLote.get(i);
      return reg == null ? _saiuEstoqueTempo(lev, u, ym) : (!!u.permInfBanco || reg <= ym);
    });
  };
  /* revisão do PR SAAS-984, comentário 4 (20/10/2026): a antecipação do PJ só existe entre a demanda e a 1ª medição —
     resposta literal "a data valida da antecipação pj é entre a demanda e a 1º medição (sem contar a garantia de obra /
     pls0) por obvio". Sem medição real (fora o PLS0) no mês da demanda ou depois, não há o que antecipar: zero. */
  const antecipEfetiva = lev.meses.some(m => m.ym >= ymDem && (m.obra || 0) > 0) ? (c.antecip || 0) : 0;
  const aporteVal = lev.aporte || 0;
  const ymAporteInf = valida(lev.dataAporte) ? ymOf(lev.dataAporte) : null;
  /* plano 3.7: "Garantia Aporte" sai na Data do Aporte (EL-132); vazia, no mês da demanda */
  const ymAporte = ymAporteInf ?? ymDem;
  /* a garantia de aporte só existe depois de depositada (e nunca antes da demanda) */
  const ymAporteAtivo = Math.max(ymDem, ymAporte);

  const F: SeriesFluxo = {
    ym: [], medEt: [], medAc: [], unidFin: [], custoMed: [], fsfLib: [], fsfDesbloq: [], fsfBloqueado: [], fracReg: [], recFin: [], remTerreno: [], remObra: [],
    remBruta: [], remIR: [], obra: [],
    antec: [], empPJ: [], pjCalc: [], libGarAporte: [], amortPJ: [], parcelaPJ: [], garAporte: [], despFin: [], tot: [], saldoDev: [],
    fsfRetido: [], garRest: [], ighMin: [], ighEstoque: [], ighOk: [],
  };
  const G = { ter: 0, ap: 0, ant: 0 };
  const garTotal = () => G.ter + G.ap + G.ant;
  /** consome as garantias na ordem terreno → aporte → antecipação; devolve o consumido e o aporte liberado */
  const consumir = (valor: number) => {
    let resta = Math.max(0, valor);
    const t = Math.min(resta, G.ter); G.ter -= t; resta -= t;
    const a = Math.min(resta, G.ap); G.ap -= a; resta -= a;
    const n = Math.min(resta, G.ant); G.ant -= n; resta -= n;
    return { consumido: t + a + n, aporte: a };
  };
  let fsfAcum = 0, saldoDev = 0, semTaxa = false;
  /* a remuneração das unidades (16/10/2026): as taxas e o aviso de histórico (memorial da remuneração, 1) */
  const rem = taxaRemuneracao(cfg.taxas);
  const primeiraAssinatura = lotes.reduce<Ym | null>((a, l) => (a == null || l.ymAssinatura < a ? l.ymAssinatura : a), null);
  const remAviso: ResultadoFluxo['remAviso'] = !lotes.length ? '' : rem.semTaxas ? 'sem-taxas'
    : (primeiraAssinatura != null && rem.inicio! > primeiraAssinatura ? 'historico-incompleto' : '');

  for (let m = 0; m < N; m++) {
    const ym = start + m;
    F.ym.push(ym);
    const et = obraAt(ym); F.medEt.push(et);
    const prevAc = m ? F.medAc[m - 1]! : (lev.pls0Pct || 0) / 100;
    const ac = prevAc + et; F.medAc.push(ac);
    F.unidFin.push(vendFinCum(ym));
    const custo = et * CTO; F.custoMed.push(custo);
    const ativo = ym >= ymDem;
    let fsfLib = 0, fsfDesbloq = 0, fracReg = 0, antec = 0, empPJ = 0, pjCalc = 0, libAp = 0, amort = 0, desp = 0, parcela = 0;
    /* 21/10/2026 — o FSF da unidade NOVA fica BLOQUEADO até o registro (memorial, Projeções: "Unidades novas já assinadas e
       ainda não registradas na data da medição têm o FSF bloqueado"; "o FSF desbloqueado amortiza o PJ"). Confirmação literal
       do usuário: "ele passa pelo fsf medição -> valor fica bloqueado -> quanto tem registro -> valor liberado + amortização".
       A CONTA do FSF não muda (o acumulado, o PJ disponibilizado, a recomposição, a amortização) — o que muda é o CAIXA: o que
       é liberado de uma nova ainda não registrada vai para o bloqueado e só entra no caixa no registro, em "FSF › Desbloqueio
       no registro". Antes (15/10), entrava no "Liberado na medição" já no processamento. */
    const liberar = (l: EstadoLote, v: number) => { if (!l.daDemanda && !l.registrada) l.bloq += v; else fsfLib += v; };
    let remTer = 0, remObr = 0, remBru = 0, remIR = 0;
    let ighMin: number | null = null, ighEst: number | null = null, ighOk: boolean | null = null;

    /* juros sobre o saldo de ABERTURA do mês (D1/D2) */
    if (ativo && params && saldoDev > 0) {
      const j = jurosDoMes(saldoDev, ym, params, cfg.taxas, adicionalDeCarencia(pa, ymTermino, ym));
      desp = j.total;
      if (j.origem === 'ausente') semTaxa = true;
    }

    if (ativo) {
      if (ym === ymDem) {
        G.ter = c.valorTerrenoGar || 0;
        G.ant = antecipEfetiva;
        antec = antecipEfetiva;
        saldoDev += antec;   /* a antecipação entra no saldo devedor na data dela (memorial, seção 1) */
      }
      if (ym === ymAporteAtivo) G.ap += aporteVal;

      /* PELO CÓDIGO DE ORIGEM (atualizar_projecao.js, recebido em 15/10/2026 — resposta literal "Sim, igual ao código
         (Recomendado)"):
           · as unidades NOVAS que assinam no mesmo dia somam o saldo e abatem as garantias juntas; o que cada uma quitou
             é a parte proporcional (`saldosParaRecomposicao[idx] -= consumidoDaUnidade`);
           · o FSF de TODAS as processadas entra no acumulado — inclusive o das novas ainda não registradas;
           · a recomposição é das novas assinadas ANTES da medição e registradas DEPOIS dela, sobre o saldo que NÃO
             quitou garantia, e só vale com PJ disponível;
           · no registro, a nova que não quitou garantia nenhuma amortiza o PJ com saldo × medição acumulada — uma saída
             de caixa (o FSF dela já tinha entrado no caixa ao longo das medições). A da demanda não amortiza. */
      const medicaoEm = ultimoDia(ym);   /* P4: a medição é no último dia do mês */

      /* (i) assinaturas das NOVAS, dia a dia: o saldo do dia abate as garantias, e cada uma fica com a sua parte */
      const porDia = new Map<string, EstadoLote[]>();
      for (const l of lotes) {
        if (l.daDemanda || l.ymAssinatura !== ym || !(l.saldo > 0)) continue;
        const dia = porDia.get(l.dataAssinatura);
        if (dia) dia.push(l); else porDia.set(l.dataAssinatura, [l]);
      }
      for (const dia of [...porDia.keys()].sort()) {
        const doDia = porDia.get(dia)!;
        const total = doDia.reduce((s, l) => s + l.saldo, 0);
        if (garTotal() <= 0 || total <= 0) continue;
        const r = consumir(total);
        libAp += r.aporte;
        for (const l of doDia) l.consumido = r.consumido * (l.saldo / total);
      }
      /* (ii) processamentos: entra de uma vez o atrasado (saldo × medição acumulada até aqui) — de todas */
      for (const l of lotes) {
        if (l.processada || l.ymProcessamento > ym || l.ymAssinatura > ym) continue;
        l.processada = true;
        const atrasado = l.saldo * prevAc;
        if (garTotal() > 0) { fsfAcum += atrasado; l.acum += atrasado; } else liberar(l, atrasado);
      }
      /* (iii) registros: a fração do terreno; a remuneração da unidade (16/10/2026); a nova que não quitou garantia
         amortiza o PJ */
      const medicoes = rem.semTaxas ? [] : medicoesDoFluxo(F.ym, F.medAc);
      for (const l of lotes) {
        if (l.ymRegistro !== ym) continue;
        fracReg += l.terreno;
        /* o FSF que estava bloqueado é liberado no registro (e, na nova que não quitou garantia, amortiza o PJ abaixo) */
        fsfDesbloq += l.bloq;
        l.bloq = 0;
        l.registrada = true;
        if (!rem.semTaxas) {
          /* as medições no último dia de cada mês (P4); antes da primeira, o PLS0 */
          const r = remuneracaoDaUnidade(l, medicoes, (lev.pls0Pct || 0) / 100, rem.taxaDoMes);
          remTer += r.terrenoLiq; remObr += r.obraLiq; remBru += r.bruta; remIR += r.ir;
        }
        if (!l.daDemanda && !(l.consumido > 0)) {
          const a = Math.min(Math.max(0, l.saldo * prevAc), saldoDev);
          amort += a;
          saldoDev -= a;
        }
      }
      /* (iv) a medição do mês */
      let recompBase = 0, fsfProcessado = 0;
      for (const l of lotes) {
        /* saldo mutuário PF do IGH e FSF acumulado: TODAS as processadas */
        if (l.processada) { fsfProcessado += l.saldo; const v = l.saldo * et; fsfAcum += v; l.acum += v; }
        if (!l.daDemanda && l.dataAssinatura < medicaoEm && l.dataRegistro > medicaoEm) {
          recompBase += Math.max(0, l.saldo - l.consumido) * et;
        }
      }
      fsfAcum = Math.max(0, fsfAcum);
      const r = consumir(custo);
      libAp += r.aporte;
      const resto = custo - r.consumido;
      if (resto > 0) {
        const usado = Math.min(fsfAcum, resto);
        /* a liberação sai do acumulado de cada lote na proporção dele (a `fracaoLiberada` do código de origem): a parte das
           novas ainda não registradas vai para o bloqueado, o resto para o caixa */
        const doLotes = lotes.reduce((s, l) => s + (l.acum > 0 ? l.acum : 0), 0);
        if (usado > 0 && doLotes > 0) {
          const f = Math.min(1, usado / doLotes);
          for (const l of lotes) { if (!(l.acum > 0)) continue; const v = l.acum * f; l.acum -= v; liberar(l, v); }
        }
        fsfAcum -= usado;
        const pjDisp = resto - usado;
        if (pjDisp > 1e-9) {
          pjCalc = pjDisp + recompBase;
          const aceito = pjCalc * (cfg.pctAceito / 100);
          const igh = ighDaMedicao(CTO, ac, fsfProcessado, saldoDev, aceito, estoqueDoFluxo(ym), pjReferencia);
          ighMin = igh.minimo; ighEst = igh.estoque; ighOk = igh.conforme;
          if (igh.conforme) { empPJ = aceito; saldoDev += aceito; }
        }
      }
    }

    /* (v) a parcela do PJ (memorial, seção 5): a partir da 1ª, saldo ÷ parcelas restantes — a última zera o saldo */
    if (ymPrimeiraParcela != null && ym >= ymPrimeiraParcela && saldoDev > 0) {
      const restantes = pa.parcelas - (ym - ymPrimeiraParcela);
      if (restantes >= 1) {
        parcela = saldoDev / restantes;
        saldoDev = restantes === 1 ? 0 : saldoDev - parcela;
        amort += parcela;
      }
    }

    F.fsfLib.push(fsfLib); F.fsfDesbloq.push(fsfDesbloq); F.fsfBloqueado.push(lotes.reduce((s, l) => s + l.bloq, 0)); F.fracReg.push(fracReg);
    F.antec.push(antec); F.empPJ.push(empPJ); F.pjCalc.push(pjCalc); F.libGarAporte.push(libAp);
    F.amortPJ.push(amort); F.parcelaPJ.push(parcela); F.despFin.push(desp);
    F.garAporte.push((aporteVal > 0 && ym === ymAporte) ? aporteVal : 0);
    /* v33 item 2: Obra = % Custo do mês × "Custo real total da obra" — saída (inalterada) */
    const custoPct = ((lev.meses[m] && lev.meses[m].obraCusto) || 0) / 100;
    F.obra.push(custoPct * (lev.custoRealObra || 0));
    /* Receitas Financeiras (16/10/2026): a remuneração LÍQUIDA das unidades registradas no mês — terreno + obra */
    F.remTerreno.push(remTer); F.remObra.push(remObr); F.recFin.push(remTer + remObr);
    F.remBruta.push(remBru); F.remIR.push(remIR);
    F.tot.push(fsfLib + fsfDesbloq + fracReg + antec + empPJ);
    F.saldoDev.push(saldoDev); F.fsfRetido.push(fsfAcum); F.garRest.push(garTotal());
    F.ighMin.push(ighMin); F.ighEstoque.push(ighEst); F.ighOk.push(ighOk);
  }

  const primeiraLib = F.ym.findIndex((_, i) => F.fsfLib[i]! + F.fsfDesbloq[i]! + F.fracReg[i]! + F.empPJ[i]! > 0);
  /* comportamento preservado do protótipo: `lev.aporte` sem guarda `||0` */
  const caixa12 = F.tot.slice(0, 12).reduce((a, v) => a + v, 0) - lev.aporte;
  const totalGeral = F.tot.reduce((a, v) => a + v, 0);
  const pjLiberado = F.empPJ.reduce((a, v) => a + v, 0) + antecipEfetiva;
  /* TOTAL líquido = entradas − saídas. Só fluxos: o saldo retido e o saldo devedor ficam fora */
  const net = F.ym.map((_, m) => F.fsfLib[m]! + F.fsfDesbloq[m]! + F.fracReg[m]! + F.recFin[m]! + F.antec[m]! + F.empPJ[m]! + F.libGarAporte[m]!
    - (F.obra[m]! + F.amortPJ[m]! + F.garAporte[m]! + F.despFin[m]!));
  const netGeral = net.reduce((a, v) => a + v, 0);
  const jurosAviso: ResultadoFluxo['jurosAviso'] = !params ? 'sem-parametros' : (semTaxa ? 'sem-taxa' : '');
  /* revisão do PR SAAS-984, comentário 4: se mesmo assim sobrar saldo no fim da tabela (o teto de 50 anos, por
     exemplo), a tabela avisa — em vez de dizer que "segue até quitar" */
  const saldoFinal = F.saldoDev.length ? F.saldoDev[F.saldoDev.length - 1]! : 0;
  const amortAviso: ResultadoFluxo['amortAviso'] = ymTermino == null ? 'sem-termino' : (saldoFinal > 0.5 ? 'saldo-final' : '');
  return { ...F, primeiraLib, caixa12, totalGeral, pjLiberado, net, netGeral, jurosAviso, ymTermino, ymPrimeiraParcela, amortAviso, remAviso };
}
